import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { db } from "../Backend/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
  getCourses,
  getSemesters,
  getQuestionSets,
  getQuestions,
  createCourse,
  createSemester,
  createQuestionSet,
  bulkAddQuestions,
  deleteQuestion,
  deleteQuestionsInSet,
  deleteQuestionSet,
  assignExam,
  getSecuritySettings,
  setSecuritySettings,
  getAllAssignments,
  deassignExam,
  renameSemester,
  deleteSemester,
  renameQuestionSet,
  addQuestion,
  updateQuestion,
} from "../Backend/examService";
import { courseFees } from "./CourseFeesData";
import { FaLock } from "react-icons/fa";
import StudentExamPortal from "./StudentExamPortal";
import StudentAnswerSheetModal from "./StudentAnswerSheetModal";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  FiUpload,
  FiPlus,
  FiBook,
  FiLayers,
  FiCheckCircle,
  FiFileText,
  FiLoader,
  FiFolder,
  FiTrash2,
  FiUsers,
  FiLock,
  FiEdit2,
} from "react-icons/fi";

const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const translateToHindi = async (text) => {
  if (!text || typeof text !== "string" || !text.trim()) return "";
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(text)}`;
    const res = await axios.get(url);
    if (res.data && res.data[0]) {
      return res.data[0].map((item) => item[0]).join("");
    }
  } catch (error) {
    console.error("Translation failed for text:", text, error);
  }
  return "";
};

const parseCustomDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.includes("/") ? dateStr.split("/") : dateStr.split("-");
  if (parts.length !== 3) return new Date(dateStr);

  let dateObj;
  if (dateStr.includes("/")) {
    let year = parseInt(parts[2]);
    if (year < 100) year += 2000;
    dateObj = new Date(year, parseInt(parts[1]) - 1, parseInt(parts[0]));
  } else {
    if (parts[0].length === 4) {
      dateObj = new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2]),
      );
    } else {
      dateObj = new Date(dateStr);
    }
  }
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

const calculateCurrentlyDueRegular = (feeBreakdown, enrollmentDateStr) => {
  if (!feeBreakdown || !enrollmentDateStr) return 0;

  let currentlyPendingRegular = 0;
  const today = new Date();

  feeBreakdown.forEach((fee) => {
    if (fee.isPaid) return;

    const isEnrollmentFee =
      fee.component_type === "Enrollment Fee" || fee.order === 1;

    let isDue = false;

    if (isEnrollmentFee) {
      isDue = true;
    } else {
      const dateObj = parseCustomDate(enrollmentDateStr);
      if (dateObj && !isNaN(dateObj.getTime())) {
        let monthsToAdd = fee.order - 1;
        if (fee.frequency === "Quarterly") {
          monthsToAdd = (fee.order - 1) * 3;
        }

        if (dateObj.getDate() > 25 && fee.order > 1) {
          monthsToAdd += 1;
        }

        const targetMonth = dateObj.getMonth() + monthsToAdd;
        dateObj.setMonth(targetMonth);
        if (dateObj.getMonth() !== targetMonth % 12) {
          dateObj.setDate(0);
        }

        if (
          dateObj.getFullYear() < today.getFullYear() ||
          (dateObj.getFullYear() === today.getFullYear() &&
            dateObj.getMonth() <= today.getMonth())
        ) {
          isDue = true;
        }
      }
    }

    if (isDue) {
      currentlyPendingRegular += fee.amount - (fee.partialPaid || 0);
    }
  });

  return currentlyPendingRegular;
};

const ExamDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [questionSets, setQuestionSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilterCourse, setSelectedFilterCourse] = useState("");
  const [selectedFilterStatus, setSelectedFilterStatus] = useState("");
  const [selectedAnswerSheetAssignment, setSelectedAnswerSheetAssignment] = useState(null);

  // Form states
  const [newSemesterName, setNewSemesterName] = useState("");
  const [newSetName, setNewSetName] = useState("");
  const [activeTab, setActiveTab] = useState("questions");
  const [assignRollNumber, setAssignRollNumber] = useState("");
  const [examDuration, setExamDuration] = useState("90");
  const [randomizeQuestions, setRandomizeQuestions] = useState(false);
  const [allowPendingFees, setAllowPendingFees] = useState(false);
  const [pinRestrictionEnabled, setPinRestrictionEnabled] = useState(false);
  const [accessPin, setAccessPin] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: "",
    id: null,
    message: "",
  });

  // Rename states
  const [editingSemesterId, setEditingSemesterId] = useState(null);
  const [editingSemesterName, setEditingSemesterName] = useState("");
  const [editingSetId, setEditingSetId] = useState(null);
  const [editingSetName, setEditingSetName] = useState("");

  // Manual Question form states
  const [manualText, setManualText] = useState("");
  const [manualTextHindi, setManualTextHindi] = useState("");
  const [manualOptA, setManualOptA] = useState("");
  const [manualOptAHindi, setManualOptAHindi] = useState("");
  const [manualOptB, setManualOptB] = useState("");
  const [manualOptBHindi, setManualOptBHindi] = useState("");
  const [manualOptC, setManualOptC] = useState("");
  const [manualOptCHindi, setManualOptCHindi] = useState("");
  const [manualOptD, setManualOptD] = useState("");
  const [manualOptDHindi, setManualOptDHindi] = useState("");
  const [manualCorrect, setManualCorrect] = useState("A");

  // Edit Question Modal States
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editQText, setEditQText] = useState("");
  const [editQTextHindi, setEditQTextHindi] = useState("");
  const [editQOptA, setEditQOptA] = useState("");
  const [editQOptAHindi, setEditQOptAHindi] = useState("");
  const [editQOptB, setEditQOptB] = useState("");
  const [editQOptBHindi, setEditQOptBHindi] = useState("");
  const [editQOptC, setEditQOptC] = useState("");
  const [editQOptCHindi, setEditQOptCHindi] = useState("");
  const [editQOptD, setEditQOptD] = useState("");
  const [editQOptDHindi, setEditQOptDHindi] = useState("");
  const [editQCorrect, setEditQCorrect] = useState("A");

  const resetManualQuestionForm = () => {
    setManualText("");
    setManualTextHindi("");
    setManualOptA("");
    setManualOptAHindi("");
    setManualOptB("");
    setManualOptBHindi("");
    setManualOptC("");
    setManualOptCHindi("");
    setManualOptD("");
    setManualOptDHindi("");
    setManualCorrect("A");
  };

  /* ================= AUTH ================= */
  const { user, isAuthenticated, isAdmin: authIsAdmin } = useAuth();
  const [authenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [studentRollNumber, setStudentRollNumber] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      setAuthenticated(true);
      setIsAdmin(authIsAdmin);
      if (!authIsAdmin && user) {
        setStudentName(user.name);
        setStudentRollNumber(user.rollNo);
      }
    } else {
      setAuthenticated(false);
      setIsAdmin(false);
    }
  }, [isAuthenticated, authIsAdmin, user]);

  useEffect(() => {
    if (authenticated && isAdmin) {
      loadSecuritySettings();
      fetchExamAssignments();

      // Silently wake up Render backend in background to prevent cold starts
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
          ? "http://localhost:5000"
          : "https://aieseci.onrender.com");
      axios.get(`${backendUrl}/health`).catch(() => { });
    }
  }, [authenticated, isAdmin]);

  const loadSecuritySettings = async () => {
    try {
      const settings = await getSecuritySettings();
      setAccessPin(settings.accessPin);
      setPinRestrictionEnabled(settings.isEnabled);
    } catch (error) {
      console.error("Failed to load security settings:", error);
    }
  };

  const fetchExamAssignments = async () => {
    try {
      setLoading(true);

      // Fetch enrollments to enrich student names (essential for legacy assignments)
      const enrollmentsRef = collection(db, "enrollments");
      const enrollmentsSnapshot = await getDocs(enrollmentsRef);
      const studentMap = {};
      enrollmentsSnapshot.docs.forEach((docSnap) => {
        const d = docSnap.data();
        if (d.rollNo) {
          studentMap[d.rollNo.toUpperCase().trim()] = d.name;
        }
      });

      const data = await getAllAssignments();
      const enriched = data.map((asg) => {
        const rollKey = (asg.rollNo || "").toUpperCase().trim();
        return {
          ...asg,
          studentName: asg.studentName || studentMap[rollKey] || "N/A",
        };
      });

      const sorted = enriched.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
      setAssignments(sorted);
    } catch (error) {
      console.error("Failed to load assignments:", error);
      toast.error("Failed to load assignments.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeassignExam = async (assignmentId) => {
    try {
      setLoading(true);
      await deassignExam(assignmentId);
      toast.success("Exam deassigned successfully!");
      await fetchExamAssignments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to deassign exam.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSecuritySettings = async () => {
    if (pinRestrictionEnabled && !accessPin.trim()) {
      toast.error("Please enter a valid PIN or disable the restriction.");
      return;
    }
    try {
      setLoading(true);
      await setSecuritySettings(accessPin.trim(), pinRestrictionEnabled);
      toast.success("Security PIN settings updated successfully!");
    } catch (error) {
      toast.error("Failed to save security settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePinRestriction = async (checked) => {
    setPinRestrictionEnabled(checked);
    try {
      await setSecuritySettings(accessPin.trim(), checked);
      toast.success(checked ? "PIN security activated!" : "PIN security deactivated.");
    } catch (error) {
      toast.error("Failed to toggle PIN security.");
      setPinRestrictionEnabled(!checked);
    }
  };

  const handleGenerateRandomPin = () => {
    const randomPin = Math.floor(100000 + Math.random() * 900000).toString();
    setAccessPin(randomPin);
    toast.info(`Generated random PIN: ${randomPin}. Save settings to apply.`);
  };

  // handleAccess is now processed globally in the navbar

  // Logout handled globally in NavBar

  useEffect(() => {
    if (authenticated) {
      fetchAndSyncCourses();
    }
  }, [authenticated]);

  const fetchAndSyncCourses = async () => {
    try {
      setLoading(true);
      const existingCourses = await getCourses();

      const allowedCourseNames = courseFees
        .map((c) => c.course_name)
        .filter(
          (name) => name !== "CCC" && name !== "cccc",
        );

      // 1. Sync Courses
      const syncedCourses = [];
      for (const name of allowedCourseNames) {
        let course;
        const existing = existingCourses.find((c) => c.name === name);

        if (!existing) {
          const docRef = await createCourse({ name });
          course = { id: docRef.id, name };
        } else {
          course = existing;
        }
        syncedCourses.push(course);
      }

      setCourses(syncedCourses);
    } catch (error) {
      console.error("DETAILED SYNC ERROR:", error);
      toast.error(`Sync Error: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = async (course) => {
    setSelectedCourse(course);
    setSelectedSemester(null);
    setSelectedSet(null);
    setSemesters([]);
    setQuestionSets([]);
    setQuestions([]);
    setLoading(true);
    try {
      let sems = await getSemesters(course.id);
      if (course.name === "O-Level" && sems.length === 0) {
        const defaultSems = ["Semester 1", "Semester 2"];
        for (const sName of defaultSems) {
          await createSemester({ name: sName, courseId: course.id });
        }
        sems = await getSemesters(course.id);
      }
      setSemesters(sems);
    } catch (error) {
      toast.error("Error loading semesters");
    } finally {
      setLoading(false);
    }
  };

  const handleSemesterSelect = async (semester) => {
    setSelectedSemester(semester);
    setSelectedSet(null);
    setQuestions([]);
    setLoading(true);
    try {
      const sets = await getQuestionSets(semester.id);
      setQuestionSets(sets);
    } catch (error) {
      toast.error("Error loading question sets");
    } finally {
      setLoading(false);
    }
  };

  const handleSetSelect = async (set) => {
    setSelectedSet(set);
    setLoading(true);
    try {
      const qs = await getQuestions(
        selectedCourse.id,
        selectedSemester.id,
        set.id,
      );
      setQuestions(qs);
    } catch (error) {
      toast.error("Error loading questions");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSetQuestions = () => {
    if (!selectedSet) return;
    setDeleteModal({
      isOpen: true,
      type: "SET_QUESTIONS",
      id: selectedSet.id,
      message:
        "Are you sure you want to delete ALL questions in this set? This action cannot be undone.",
    });
  };

  const handleDeleteSingleQuestion = (questionId) => {
    setDeleteModal({
      isOpen: true,
      type: "SINGLE_QUESTION",
      id: questionId,
      message: "Are you sure you want to delete this specific question?",
    });
  };

  const handleDeleteSet = (setId, e) => {
    e.stopPropagation(); // prevent triggering select
    setDeleteModal({
      isOpen: true,
      type: "ENTIRE_SET",
      id: setId,
      message:
        "Are you sure you want to delete this entire Question Set and ALL its questions?",
    });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteModal;
    setDeleteModal({ ...deleteModal, isOpen: false });

    if (type === "SET_QUESTIONS") {
      setLoading(true);
      try {
        await deleteQuestionsInSet(id);
        setQuestions([]);
        toast.success("All questions deleted successfully");
      } catch (error) {
        toast.error("Failed to delete questions");
      } finally {
        setLoading(false);
      }
    } else if (type === "SINGLE_QUESTION") {
      try {
        await deleteQuestion(id);
        setQuestions(questions.filter((q) => q.id !== id));
        toast.success("Question deleted");
      } catch (error) {
        toast.error("Failed to delete question");
      }
    } else if (type === "ENTIRE_SET") {
      setLoading(true);
      try {
        await deleteQuestionSet(id);
        setQuestionSets(questionSets.filter((s) => s.id !== id));
        if (selectedSet?.id === id) {
          setSelectedSet(null);
          setQuestions([]);
        }
        toast.success("Question set deleted");
      } catch (error) {
        toast.error("Failed to delete set");
      } finally {
        setLoading(false);
      }
    } else if (type === "ENTIRE_SEMESTER") {
      setLoading(true);
      try {
        await deleteSemester(id);
        setSemesters(semesters.filter((sem) => sem.id !== id));
        if (selectedSemester?.id === id) {
          setSelectedSemester(null);
          setSelectedSet(null);
          setQuestionSets([]);
          setQuestions([]);
        }
        toast.success("Semester and all its cascading contents deleted successfully");
      } catch (error) {
        toast.error("Failed to delete semester");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSemesterPrompt = (semester, e) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      type: "ENTIRE_SEMESTER",
      id: semester.id,
      message: `Are you sure you want to delete "${semester.name}"? This will delete the semester, ALL its question sets, and ALL questions inside those sets.`,
    });
  };

  const handleRenameSemesterPrompt = (semester, e) => {
    e.stopPropagation();
    setEditingSemesterId(semester.id);
    setEditingSemesterName(semester.name);
  };

  const handleSaveSemesterRename = async (semesterId) => {
    if (!editingSemesterName.trim()) {
      toast.error("Semester name cannot be empty");
      return;
    }
    setLoading(true);
    try {
      await renameSemester(semesterId, editingSemesterName.trim());
      setSemesters(
        semesters.map((sem) =>
          sem.id === semesterId ? { ...sem, name: editingSemesterName.trim() } : sem
        )
      );
      if (selectedSemester?.id === semesterId) {
        setSelectedSemester((prev) => ({ ...prev, name: editingSemesterName.trim() }));
      }
      setEditingSemesterId(null);
      toast.success("Semester renamed successfully");
    } catch (error) {
      toast.error("Failed to rename semester");
    } finally {
      setLoading(false);
    }
  };

  const handleRenameSetPrompt = (set, e) => {
    e.stopPropagation();
    setEditingSetId(set.id);
    setEditingSetName(set.title);
  };

  const handleSaveSetRename = async (setId) => {
    if (!editingSetName.trim()) {
      toast.error("Question set name cannot be empty");
      return;
    }
    setLoading(true);
    try {
      await renameQuestionSet(setId, editingSetName.trim());
      setQuestionSets(
        questionSets.map((s) =>
          s.id === setId ? { ...s, title: editingSetName.trim() } : s
        )
      );
      if (selectedSet?.id === setId) {
        setSelectedSet((prev) => ({ ...prev, title: editingSetName.trim() }));
      }
      setEditingSetId(null);
      toast.success("Question set renamed successfully");
    } catch (error) {
      toast.error("Failed to rename question set");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSemester = async () => {
    if (!newSemesterName || !selectedCourse) return;
    try {
      await createSemester({
        name: newSemesterName,
        courseId: selectedCourse.id,
      });
      setNewSemesterName("");
      const sems = await getSemesters(selectedCourse.id);
      setSemesters(sems);
      toast.success("Semester created");
    } catch (error) {
      toast.error("Failed to create semester");
    }
  };

  const handleAddSet = async () => {
    if (!newSetName || !selectedSemester) return;
    try {
      await createQuestionSet({
        title: newSetName,
        semesterId: selectedSemester.id,
      });
      setNewSetName("");
      const sets = await getQuestionSets(selectedSemester.id);
      setQuestionSets(sets);
      toast.success("Question set created");
    } catch (error) {
      toast.error("Failed to create set");
    }
  };

  const handleOpenEditQuestionModal = (q) => {
    setEditingQuestion(q);
    setEditQText(q.text || "");
    setEditQTextHindi(q.textHindi || "");
    setEditQOptA(q.options?.A || "");
    setEditQOptAHindi(q.optionsHindi?.A || "");
    setEditQOptB(q.options?.B || "");
    setEditQOptBHindi(q.optionsHindi?.B || "");
    setEditQOptC(q.options?.C || "");
    setEditQOptCHindi(q.optionsHindi?.C || "");
    setEditQOptD(q.options?.D || "");
    setEditQOptDHindi(q.optionsHindi?.D || "");
    setEditQCorrect(q.correctAnswer || "A");
    setShowEditQuestionModal(true);
  };

  const handleSaveEditedQuestion = async (e) => {
    if (e) e.preventDefault();
    if (!editingQuestion) return;

    if (!editQText.trim()) {
      toast.error("Question text is required.");
      return;
    }
    if (!editQOptA.trim() || !editQOptB.trim() || !editQOptC.trim() || !editQOptD.trim()) {
      toast.error("All four options (A, B, C, D) are required.");
      return;
    }

    setLoading(true);
    try {
      toast.info("Saving question updates...");

      const qHindi = editQTextHindi.trim()
        ? editQTextHindi.trim()
        : await translateToHindi(editQText.trim());

      const optAHindi = editQOptAHindi.trim()
        ? editQOptAHindi.trim()
        : await translateToHindi(editQOptA.trim());

      const optBHindi = editQOptBHindi.trim()
        ? editQOptBHindi.trim()
        : await translateToHindi(editQOptB.trim());

      const optCHindi = editQOptCHindi.trim()
        ? editQOptCHindi.trim()
        : await translateToHindi(editQOptC.trim());

      const optDHindi = editQOptDHindi.trim()
        ? editQOptDHindi.trim()
        : await translateToHindi(editQOptD.trim());

      const updatedData = {
        text: editQText.trim(),
        textHindi: qHindi,
        options: {
          A: editQOptA.trim(),
          B: editQOptB.trim(),
          C: editQOptC.trim(),
          D: editQOptD.trim(),
        },
        optionsHindi: {
          A: optAHindi,
          B: optBHindi,
          C: optCHindi,
          D: optDHindi,
        },
        correctAnswer: editQCorrect,
      };

      await updateQuestion(editingQuestion.id, updatedData);
      toast.success("Question updated successfully!");
      setShowEditQuestionModal(false);
      setEditingQuestion(null);

      // Reload questions
      if (selectedCourse && selectedSemester && selectedSet) {
        const qs = await getQuestions(
          selectedCourse.id,
          selectedSemester.id,
          selectedSet.id
        );
        setQuestions(qs);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update question.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveManualQuestion = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCourse || !selectedSemester || !selectedSet) {
      toast.error("Please select a course, semester, and set first.");
      return;
    }

    if (!manualText.trim()) {
      toast.error("Question text is required.");
      return;
    }
    if (!manualOptA.trim() || !manualOptB.trim() || !manualOptC.trim() || !manualOptD.trim()) {
      toast.error("All four options (A, B, C, D) are required.");
      return;
    }

    setLoading(true);
    try {
      // Auto-translate to Hindi if fields are left blank
      toast.info("Translating missing Hindi fields...");

      const qHindi = manualTextHindi.trim()
        ? manualTextHindi.trim()
        : await translateToHindi(manualText.trim());

      const optAHindi = manualOptAHindi.trim()
        ? manualOptAHindi.trim()
        : await translateToHindi(manualOptA.trim());

      const optBHindi = manualOptBHindi.trim()
        ? manualOptBHindi.trim()
        : await translateToHindi(manualOptB.trim());

      const optCHindi = manualOptCHindi.trim()
        ? manualOptCHindi.trim()
        : await translateToHindi(manualOptC.trim());

      const optDHindi = manualOptDHindi.trim()
        ? manualOptDHindi.trim()
        : await translateToHindi(manualOptD.trim());

      const newQuestionData = {
        text: manualText.trim(),
        textHindi: qHindi,
        options: {
          A: manualOptA.trim(),
          B: manualOptB.trim(),
          C: manualOptC.trim(),
          D: manualOptD.trim()
        },
        optionsHindi: {
          A: optAHindi,
          B: optBHindi,
          C: optCHindi,
          D: optDHindi
        },
        correctAnswer: manualCorrect,
        courseId: selectedCourse.id,
        semesterId: selectedSemester.id,
        setId: selectedSet.id
      };

      await addQuestion(newQuestionData);
      toast.success("Question manually added successfully!");
      resetManualQuestionForm();

      // Reload questions
      const qs = await getQuestions(
        selectedCourse.id,
        selectedSemester.id,
        selectedSet.id
      );
      setQuestions(qs);

      // Switch back to questions list
      setActiveTab("questions");
    } catch (error) {
      console.error(error);
      toast.error("Failed to add question.");
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCourse || !selectedSemester) return;

    const formData = new FormData();
    formData.append("pdf", file);

    setLoading(true);
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
          ? "http://localhost:5000"
          : "https://aieseci.onrender.com");
      const response = await axios.post(
        `${backendUrl}/api/parse-pdf`,
        formData,
      );
      if (response.data.success) {
        const parsed = response.data.questions;
        console.log("Raw PDF Text extracted by backend:\n", response.data.text);

        if (parsed.length === 0) {
          toast.warn(
            "No questions could be found in the PDF. Please check the format.",
          );
        } else {
          // Translate to Hindi in parallel
          toast.info("Translating questions and choices to Hindi...");
          const translatedQuestions = await Promise.all(
            parsed.map(async (q) => {
              const textHindi = await translateToHindi(q.text);
              const optionsHindi = {};
              if (q.options) {
                for (const [key, val] of Object.entries(q.options)) {
                  optionsHindi[key] = await translateToHindi(val);
                }
              }
              return {
                ...q,
                textHindi,
                optionsHindi,
              };
            })
          );

          await bulkAddQuestions(
            translatedQuestions,
            selectedCourse.id,
            selectedSemester.id,
            selectedSet?.id,
          );
          toast.success(`Successfully uploaded and translated ${parsed.length} questions!`);
          // Refresh questions
          const qs = await getQuestions(
            selectedCourse.id,
            selectedSemester.id,
            selectedSet?.id,
          );
          setQuestions(qs);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Error parsing PDF. Make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignExam = async () => {
    if (!selectedCourse || !selectedSemester || !selectedSet) {
      toast.error("Please select a Question Set first.");
      return;
    }
    if (!assignRollNumber.trim()) {
      toast.error("Please enter a student roll number.");
      return;
    }

    try {
      setLoading(true);
      const formattedRoll = assignRollNumber.toUpperCase().includes("AFT-")
        ? assignRollNumber.replace(/\s+/g, "").toUpperCase()
        : `AFT-${assignRollNumber}`.replace(/\s+/g, "").toUpperCase();

      // Check Enrollment and Fees
      const enrollmentsRef = collection(db, "enrollments");
      const q = query(enrollmentsRef, where("rollNo", "==", formattedRoll));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        toast.error("Student not found in enrollment records.");
        setLoading(false);
        return;
      }

      const studentData = snapshot.docs[0].data();

      // Check Course Match
      if (studentData.course !== selectedCourse.name) {
        toast.error(
          `Cannot assign: Student is enrolled in ${studentData.course}, not ${selectedCourse.name}.`,
        );
        setLoading(false);
        return;
      }

      // Check Fees Match
      if (!studentData.feeBreakdown || !studentData.feeBreakdown.length) {
        toast.error(
          "Student fee ledger not found. Please generate ledger first.",
        );
        setLoading(false);
        return;
      }

      if (!allowPendingFees) {
        const overdueDues = calculateCurrentlyDueRegular(
          studentData.feeBreakdown,
          studentData.dateOfEnrollment,
        );

        if (overdueDues > 0) {
          toast.error(
            `Cannot assign exam: Student has overdue fees of ₹${overdueDues} up to this month. (Check 'Bypass Fee Check' to override)`,
          );
          setLoading(false);
          return;
        }
      }

      // Check Duplicate Assignment
      const assignmentsRef = collection(db, "exam_assignments");
      const dupQuery = query(
        assignmentsRef,
        where("rollNo", "==", formattedRoll),
        where("setId", "==", selectedSet.id)
      );
      const dupSnapshot = await getDocs(dupQuery);

      if (!dupSnapshot.empty) {
        toast.error("This exam has already been assigned to this student.");
        setLoading(false);
        return;
      }

      // Fetch all questions in the set to get the count
      const qs = await getQuestions(
        selectedCourse.id,
        selectedSemester.id,
        selectedSet.id
      );
      const totalQuestionsCount = qs.length;

      let questionOrder = null;
      if (randomizeQuestions && qs.length > 0) {
        const shuffledQs = shuffleArray(qs);
        questionOrder = shuffledQs.map((q) => q.id);
      }

      await assignExam({
        rollNo: formattedRoll,
        studentName: studentData.name || "",
        courseId: selectedCourse.id,
        courseName: selectedCourse.name,
        semesterId: selectedSemester.id,
        semesterName: selectedSemester.name,
        setId: selectedSet.id,
        setTitle: selectedSet.title,
        duration: parseInt(examDuration) || 90,
        totalQuestions: totalQuestionsCount,
        ...(questionOrder && { questionOrder }),
      });

      toast.success(`Exam successfully assigned to ${formattedRoll}`);
      setAssignRollNumber("");
      setExamDuration("90");
      await fetchExamAssignments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to assign exam.");
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6 text-white font-sans">
        <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-3xl p-8 text-center shadow-xl">
          <div className="w-16 h-16 bg-gray-900 border border-gray-700/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FaLock className="text-3xl text-yellow-400" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Exam Portal Locked</h2>
          <p className="text-gray-400 mb-6 font-medium leading-relaxed">
            Please log in using the button in the navigation bar to access the Exam Dashboard or complete your assignments.
          </p>
        </div>
      </div>
    );
  }

  const filteredAssignments = assignments.filter((asg) => {
    const queryStr = searchQuery.toLowerCase().trim();
    const matchesSearch =
      queryStr === "" ||
      (asg.studentName || "").toLowerCase().includes(queryStr) ||
      (asg.rollNo || "").toLowerCase().includes(queryStr) ||
      (asg.setTitle || "").toLowerCase().includes(queryStr);

    const matchesCourse =
      selectedFilterCourse === "" ||
      asg.courseName === selectedFilterCourse;

    const matchesStatus =
      selectedFilterStatus === "" ||
      asg.status === selectedFilterStatus;

    return matchesSearch && matchesCourse && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#0f172a] text-white p-3 sm:p-6 md:p-8 font-sans overflow-x-hidden">
      <ToastContainer theme="dark" />

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6 sm:mb-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Exam Management Portal
          </h1>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">
            {isAdmin
              ? "Design courses, question sets, and upload assessments with ease."
              : "View your available courses and exam questions."}
          </p>
        </div>
      </div>

      {isAdmin ? (
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar: Courses */}
          <div className="lg:col-span-1 space-y-6 min-w-0">
            <div className="bg-gray-800/50 backdrop-blur-md p-6 rounded-3xl border border-gray-700 shadow-xl">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <FiBook className="text-blue-400" /> Courses
              </h2>
              <div className="space-y-2">
                {courses.map((course) => (
                  <button
                    key={course.id}
                    onClick={() => handleCourseSelect(course)}
                    className={`w-full text-left p-3 rounded-xl transition-all ${selectedCourse?.id === course.id
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                        : "hover:bg-gray-700/50 text-gray-400"
                      }`}
                  >
                    {course.name}
                  </button>
                ))}
              </div>
            </div>

            {selectedCourse && (
              <div className="bg-gray-800/50 backdrop-blur-md p-6 rounded-3xl border border-gray-700 shadow-xl animate-in fade-in slide-in-from-top-4">
                <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                  <FiFolder className="text-purple-400" /> Semesters
                </h2>
                <div className="space-y-2">
                  {semesters.map((semester) => {
                    const isEditing = editingSemesterId === semester.id;
                    return (
                      <div key={semester.id} className="flex gap-2 items-center w-full">
                        {isEditing ? (
                          <div className="flex gap-1.5 w-full items-center">
                            <input
                              type="text"
                              value={editingSemesterName}
                              onChange={(e) => setEditingSemesterName(e.target.value)}
                              className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm w-full outline-none focus:ring-1 focus:ring-purple-500 text-white"
                              onKeyDown={(e) => e.key === "Enter" && handleSaveSemesterRename(semester.id)}
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveSemesterRename(semester.id)}
                              className="bg-green-600 hover:bg-green-500 text-white px-2.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingSemesterId(null)}
                              className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleSemesterSelect(semester)}
                              className={`w-full text-left p-3 rounded-xl transition-all overflow-hidden text-ellipsis whitespace-nowrap ${selectedSemester?.id === semester.id
                                  ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20"
                                  : "hover:bg-gray-700/50 text-gray-400"
                                }`}
                            >
                              {semester.name}
                            </button>
                            {isAdmin && (
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={(e) => handleRenameSemesterPrompt(semester, e)}
                                  className="bg-gray-800 hover:bg-blue-500/20 text-gray-400 hover:text-blue-400 border border-gray-700 hover:border-blue-500/50 p-2.5 rounded-xl transition-all"
                                  title="Rename Semester"
                                >
                                  <FiEdit2 size={16} />
                                </button>
                                <button
                                  onClick={(e) => handleDeleteSemesterPrompt(semester, e)}
                                  className="bg-gray-800 hover:bg-red-500/20 text-gray-400 hover:text-red-400 border border-gray-700 hover:border-red-500/50 p-2.5 rounded-xl transition-all"
                                  title="Delete Semester"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                {isAdmin && (
                  <div className="mt-4 flex gap-2">
                    <input
                      type="text"
                      placeholder="New Semester..."
                      className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm w-full outline-none focus:ring-1 focus:ring-purple-500"
                      value={newSemesterName}
                      onChange={(e) => setNewSemesterName(e.target.value)}
                    />
                    <button
                      onClick={handleAddSemester}
                      className="bg-purple-600 p-2 rounded-xl hover:bg-purple-500"
                    >
                      <FiPlus />
                    </button>
                  </div>
                )}
              </div>
            )}

            {selectedSemester && (
              <div className="bg-gray-800/50 backdrop-blur-md p-6 rounded-3xl border border-gray-700 shadow-xl animate-in fade-in slide-in-from-top-4">
                <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                  <FiLayers className="text-pink-400" /> Question Sets
                </h2>
                <div className="space-y-2">
                  {questionSets.map((set) => {
                    const isEditing = editingSetId === set.id;
                    return (
                      <div key={set.id} className="flex gap-2 items-center w-full">
                        {isEditing ? (
                          <div className="flex gap-1.5 w-full items-center">
                            <input
                              type="text"
                              value={editingSetName}
                              onChange={(e) => setEditingSetName(e.target.value)}
                              className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm w-full outline-none focus:ring-1 focus:ring-pink-500 text-white"
                              onKeyDown={(e) => e.key === "Enter" && handleSaveSetRename(set.id)}
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveSetRename(set.id)}
                              className="bg-green-600 hover:bg-green-500 text-white px-2.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingSetId(null)}
                              className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleSetSelect(set)}
                              className={`w-full text-left p-3 rounded-xl transition-all overflow-hidden text-ellipsis whitespace-nowrap ${selectedSet?.id === set.id
                                  ? "bg-pink-600 text-white shadow-lg shadow-pink-500/20"
                                  : "hover:bg-gray-700/50 text-gray-400"
                                }`}
                            >
                              {set.title}
                            </button>
                            {isAdmin && (
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={(e) => handleRenameSetPrompt(set, e)}
                                  className="bg-gray-800 hover:bg-blue-500/20 text-gray-400 hover:text-blue-400 border border-gray-700 hover:border-blue-500/50 p-2.5 rounded-xl transition-all"
                                  title="Rename Set"
                                >
                                  <FiEdit2 size={16} />
                                </button>
                                <button
                                  onClick={(e) => handleDeleteSet(set.id, e)}
                                  className="bg-gray-800 hover:bg-red-500/20 text-gray-400 hover:text-red-400 border border-gray-700 hover:border-red-500/50 p-2.5 rounded-xl transition-all"
                                  title="Delete Set"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                {isAdmin && (
                  <div className="mt-4 flex gap-2">
                    <input
                      type="text"
                      placeholder="New Set..."
                      className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm w-full outline-none focus:ring-1 focus:ring-pink-500"
                      value={newSetName}
                      onChange={(e) => setNewSetName(e.target.value)}
                    />
                    <button
                      onClick={handleAddSet}
                      className="bg-pink-600 p-2 rounded-xl hover:bg-pink-500"
                    >
                      <FiPlus />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* PIN Security Settings Card */}
            {isAdmin && (
              <div className="bg-gray-800/50 backdrop-blur-md p-6 rounded-3xl border border-gray-700 shadow-xl space-y-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FiLock className="text-red-400" /> Exam Security
                </h2>
                <div className="space-y-4">
                  <label className="flex items-center justify-between cursor-pointer select-none">
                    <span className="text-sm font-semibold text-gray-300">
                      Require Access PIN
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={pinRestrictionEnabled}
                        onChange={(e) => handleTogglePinRestriction(e.target.checked)}
                      />
                      <div className={`w-10 h-6 rounded-full transition-colors ${pinRestrictionEnabled ? "bg-red-500" : "bg-gray-700"}`}></div>
                      <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${pinRestrictionEnabled ? "translate-x-4" : ""}`}></div>
                    </div>
                  </label>

                  {pinRestrictionEnabled && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                      <input
                        type="text"
                        placeholder="Access PIN"
                        value={accessPin}
                        onChange={(e) => setAccessPin(e.target.value)}
                        className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm w-full outline-none focus:ring-1 focus:ring-red-500 text-white text-center font-bold tracking-widest"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveSecuritySettings}
                          className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-xl text-xs transition-all shadow-lg shadow-red-500/20"
                        >
                          Save Settings
                        </button>
                        <button
                          onClick={handleGenerateRandomPin}
                          className="bg-gray-700 hover:bg-gray-600 text-gray-300 py-2 px-3 rounded-xl text-xs transition-all"
                          title="Generate a random 6-digit PIN"
                        >
                          Random PIN
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6 min-w-0">
            {!selectedCourse ? (
              <div className="bg-gray-800/30 border-2 border-dashed border-gray-700 rounded-3xl h-[350px] sm:h-[400px] flex flex-col items-center justify-center text-gray-500 p-6 text-center">
                <FiBook size={56} className="mb-4 opacity-20" />
                <p className="text-lg sm:text-xl">
                  Select a course to start managing questions
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Stats / Header */}
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-5 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl relative overflow-hidden">
                  <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight break-words">
                        {selectedCourse.name}
                      </h2>
                      <p className="text-blue-100 opacity-80 text-sm sm:text-base">
                        {selectedSet
                          ? selectedSet.title
                          : "Select a Question Set to view questions"}
                      </p>
                      <div className="mt-4 sm:mt-6 flex gap-4">
                        <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl">
                          <p className="text-xs font-bold uppercase opacity-60">
                            Total Questions
                          </p>
                          <p className="text-2xl font-black">
                            {questions.length}
                          </p>
                        </div>
                      </div>
                    </div>
                    {isAdmin && selectedSet && questions.length > 0 && (
                      <button
                        onClick={handleDeleteSetQuestions}
                        className="w-full sm:w-auto justify-center flex items-center gap-2 bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white border border-red-500/50 px-4 py-2 rounded-xl transition-all text-xs sm:text-sm"
                      >
                        <FiTrash2 /> Delete All Questions
                      </button>
                    )}
                  </div>
                  <FiFileText className="absolute -right-4 -bottom-4 text-white/5 w-48 h-48 pointer-events-none" />
                </div>

                {/* Assign Exam UI */}
                {isAdmin && selectedSet && (
                  <div className="bg-gray-800/50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-700 shadow-xl space-y-4">
                    <h3 className="text-base sm:text-lg font-bold flex items-center gap-2">
                      <FiUsers className="text-blue-400" /> Assign Exam to Student
                    </h3>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex items-center flex-1 bg-gray-900 border border-gray-700 rounded-xl focus-within:ring-1 focus-within:ring-blue-500 transition-all overflow-hidden">
                          <span className="pl-4 pr-1 text-gray-400 font-bold select-none text-sm">
                            AFT-
                          </span>
                          <input
                            type="text"
                            placeholder="101"
                            value={assignRollNumber.replace(/^AFT-?/i, "")}
                            onChange={(e) => {
                              const val = e.target.value.replace(/^AFT-?/i, "");
                              setAssignRollNumber(val ? `AFT-${val}` : "");
                            }}
                            className="w-full py-3 pr-3 bg-transparent text-white outline-none text-sm"
                          />
                        </div>
                        <div className="flex items-center w-full sm:w-48 bg-gray-900 border border-gray-700 rounded-xl focus-within:ring-1 focus-within:ring-blue-500 transition-all overflow-hidden">
                          <span className="pl-4 pr-1 text-gray-400 font-bold select-none text-sm">
                            Duration:
                          </span>
                          <input
                            type="number"
                            placeholder="90"
                            value={examDuration}
                            onChange={(e) => setExamDuration(e.target.value)}
                            className="w-full py-3 pr-3 bg-transparent text-white outline-none text-sm text-center font-bold"
                            min="1"
                          />
                          <span className="pr-4 text-gray-400 text-sm font-bold select-none">
                            Min
                          </span>
                        </div>
                        <button
                          onClick={handleAssignExam}
                          disabled={loading || !assignRollNumber || !examDuration}
                          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-3 rounded-xl disabled:opacity-50 transition-all shadow-lg shadow-blue-500/20 text-sm"
                        >
                          Assign Exam
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-6">
                        {/* Randomizer Toggle */}
                        <label className="flex items-center gap-3 cursor-pointer group w-fit select-none">
                          <div className="relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={randomizeQuestions}
                              onChange={(e) => setRandomizeQuestions(e.target.checked)}
                            />
                            <div className={`w-10 h-6 rounded-full transition-colors ${randomizeQuestions ? "bg-blue-500" : "bg-gray-700"}`}></div>
                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${randomizeQuestions ? "translate-x-4" : ""}`}></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-300 group-hover:text-white transition-colors">
                            Randomize question order for this student
                          </span>
                        </label>

                        {/* Bypass Fee Check Toggle */}
                        <label className="flex items-center gap-3 cursor-pointer group w-fit select-none">
                          <div className="relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={allowPendingFees}
                              onChange={(e) => setAllowPendingFees(e.target.checked)}
                            />
                            <div className={`w-10 h-6 rounded-full transition-colors ${allowPendingFees ? "bg-yellow-500" : "bg-gray-700"}`}></div>
                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${allowPendingFees ? "translate-x-4" : ""}`}></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-300 group-hover:text-white transition-colors">
                            Bypass Fee Check (Allow exam assignment with pending dues)
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tabs */}
                <div className="w-full overflow-x-auto pb-1.5 scrollbar-thin" style={{ WebkitOverflowScrolling: "touch" }}>
                  <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 bg-gray-800/60 rounded-2xl border border-gray-700/80 w-max min-w-full sm:min-w-0 sm:w-fit">
                    <button
                      onClick={() => setActiveTab("questions")}
                      className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all shrink-0 whitespace-nowrap ${activeTab === "questions" ? "bg-gray-700 text-white shadow" : "text-gray-400 hover:text-gray-200"
                        }`}
                    >
                      Questions List
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => setActiveTab("upload")}
                        className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all shrink-0 whitespace-nowrap ${activeTab === "upload" ? "bg-gray-700 text-white shadow" : "text-gray-400 hover:text-gray-200"
                          }`}
                      >
                        Upload PDF
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setActiveTab("add-question");
                          resetManualQuestionForm();
                        }}
                        className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all shrink-0 whitespace-nowrap ${activeTab === "add-question" ? "bg-gray-700 text-white shadow" : "text-gray-400 hover:text-gray-200"
                          }`}
                      >
                        Add Question
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setActiveTab("assignments");
                          fetchExamAssignments();
                        }}
                        className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all shrink-0 whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${activeTab === "assignments"
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                            : "text-gray-400 hover:text-gray-200"
                          }`}
                      >
                        <FiUsers size={15} className="shrink-0" />
                        <span>Submissions & Answer Sheets</span>
                        {assignments.filter((a) => a.status === "completed").length > 0 && (
                          <span className="bg-emerald-500 text-gray-950 font-black text-[10px] px-2 py-0.5 rounded-full shrink-0">
                            {assignments.filter((a) => a.status === "completed").length} Done
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="min-h-[500px]">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-blue-400">
                      <FiLoader className="animate-spin mb-4" size={48} />
                      <p>Processing data...</p>
                    </div>
                  ) : activeTab === "questions" ? (
                    <div className="grid grid-cols-1 gap-4">
                      {questions.length === 0 ? (
                        <p className="text-center text-gray-500 py-20">
                          No questions found for this selection.
                        </p>
                      ) : (
                        questions.map((q, idx) => (
                          <div
                            key={q.id}
                            className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700 hover:border-blue-500/50 transition-all group"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <span className="text-xs font-bold text-blue-400 bg-blue-400/10 px-2 py-1 rounded-md uppercase tracking-wider">
                                Question {idx + 1}
                              </span>
                              <div className="flex gap-3 items-center">
                                <FiCheckCircle
                                  className={
                                    q.correctAnswer
                                      ? "text-green-400"
                                      : "text-gray-600"
                                  }
                                />
                                {isAdmin && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleOpenEditQuestionModal(q)
                                      }
                                      className="text-gray-500 hover:text-blue-400 transition-colors"
                                      title="Edit Question"
                                    >
                                      <FiEdit2 size={18} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteSingleQuestion(q.id)
                                      }
                                      className="text-gray-500 hover:text-red-400 transition-colors"
                                      title="Delete Question"
                                    >
                                      <FiTrash2 size={18} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <p className="text-lg font-medium text-gray-100 mb-4">
                              {q.text}
                              {q.textHindi && (
                                <span className="block text-sm text-gray-400 font-semibold italic mt-2 leading-relaxed">
                                  {q.textHindi}
                                </span>
                              )}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {Object.entries(q.options || {}).map(
                                ([key, val]) => (
                                  <div
                                    key={key}
                                    className={`p-3 rounded-xl border ${q.correctAnswer === key
                                        ? "bg-green-500/10 border-green-500/50 text-green-200"
                                        : "bg-gray-900/50 border-gray-700 text-gray-400"
                                      }`}
                                  >
                                    <span className="font-black mr-2">
                                      {key}.
                                    </span>{" "}
                                    <span>{val}</span>
                                    {q.optionsHindi?.[key] && (
                                      <span className="block text-xs text-gray-500 mt-1 italic font-semibold">
                                        {q.optionsHindi[key]}
                                      </span>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : activeTab === "upload" ? (
                    <div className="bg-gray-800/50 border-2 border-dashed border-gray-700 rounded-3xl p-12 flex flex-col items-center justify-center text-center">
                      <FiUpload size={48} className="text-blue-400 mb-6" />
                      <h3 className="text-2xl font-bold mb-2">
                        Upload Question PDF
                      </h3>
                      <p className="text-gray-400 max-w-md mb-8">
                        Upload a PDF file containing MCQs. Our AI will
                        automatically parse the questions, options, and correct
                        answers.
                      </p>
                      <label className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-2xl cursor-pointer shadow-lg shadow-blue-500/20 transition-all active:scale-95">
                        Select PDF File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf"
                          onChange={handlePdfUpload}
                        />
                      </label>
                    </div>
                  ) : activeTab === "assignments" ? (
                    <div className="bg-gray-800/50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-700 shadow-xl overflow-hidden">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                            <FiUsers className="text-blue-400 shrink-0" /> Student Submissions & Answer Sheets
                          </h3>
                          <p className="text-xs text-gray-400 mt-1">
                            Review student examination submissions, performance scores, and inspect detailed answer sheets.
                          </p>
                        </div>

                        {/* Search and Filters Controls */}
                        <div className="flex flex-col sm:flex-row gap-2.5 w-full md:w-auto flex-wrap">
                          {/* Search bar */}
                          <div className="flex items-center bg-gray-900 border border-gray-750 rounded-xl px-3 py-2 w-full sm:w-56 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                            <svg className="w-4 h-4 text-gray-400 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              placeholder="Search student, roll, or set..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white outline-none w-full text-xs"
                            />
                          </div>

                          {/* Status dropdown */}
                          <select
                            value={selectedFilterStatus}
                            onChange={(e) => setSelectedFilterStatus(e.target.value)}
                            className="bg-gray-900 border border-gray-750 text-white text-xs font-semibold rounded-xl px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer w-full sm:w-40"
                          >
                            <option value="">All Statuses</option>
                            <option value="completed">Completed (Sheet Ready)</option>
                            <option value="in_progress">In Progress</option>
                            <option value="pending">Pending</option>
                          </select>

                          {/* Course dropdown */}
                          <select
                            value={selectedFilterCourse}
                            onChange={(e) => setSelectedFilterCourse(e.target.value)}
                            className="bg-gray-900 border border-gray-750 text-white text-xs font-semibold rounded-xl px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer w-full sm:w-40"
                          >
                            <option value="">All Courses</option>
                            {courses.map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {assignments.length === 0 ? (
                        <p className="text-center text-gray-500 py-20">No active or completed exam assignments found.</p>
                      ) : filteredAssignments.length === 0 ? (
                        <p className="text-center text-gray-500 py-20">No assignments match your search or filter.</p>
                      ) : (
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                          <table className="w-full text-left border-collapse min-w-[650px]">
                            <thead>
                              <tr className="border-b border-gray-700 text-gray-400 text-xs sm:text-sm font-bold">
                                <th className="pb-3 pr-4 whitespace-nowrap">Student</th>
                                <th className="pb-3 pr-4 whitespace-nowrap">Course & Sem</th>
                                <th className="pb-3 pr-4 whitespace-nowrap">Exam Set</th>
                                <th className="pb-3 pr-4 text-center whitespace-nowrap">Duration</th>
                                <th className="pb-3 pr-4 text-center whitespace-nowrap">Status</th>
                                <th className="pb-3 pr-4 text-center whitespace-nowrap">Score</th>
                                <th className="pb-3 text-right whitespace-nowrap">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800 text-xs sm:text-sm">
                              {filteredAssignments.map((asg) => (
                                <tr key={asg.id} className="hover:bg-gray-900/30 transition-all">
                                  <td className="py-3.5 pr-4 whitespace-nowrap">
                                    <div className="font-bold text-white">{asg.studentName || "N/A"}</div>
                                    <div className="text-xs text-yellow-500 font-mono mt-0.5">{asg.rollNo}</div>
                                  </td>
                                  <td className="py-3.5 pr-4 whitespace-nowrap">
                                    <div className="text-gray-200">{asg.courseName}</div>
                                    <div className="text-xs text-gray-500">{asg.semesterName}</div>
                                  </td>
                                  <td className="py-3.5 pr-4 whitespace-nowrap">
                                    <div className="text-blue-300 font-semibold">{asg.setTitle}</div>
                                  </td>
                                  <td className="py-3.5 pr-4 text-center font-medium whitespace-nowrap">{asg.duration || 90} Min</td>
                                  <td className="py-3.5 pr-4 text-center whitespace-nowrap">
                                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${asg.status === "completed"
                                        ? "bg-green-500/10 text-green-400 border border-green-500/25"
                                        : asg.status === "in_progress"
                                          ? "bg-orange-500/10 text-orange-400 border border-orange-500/25 animate-pulse"
                                          : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/25"
                                      }`}>
                                      {asg.status === "completed"
                                        ? "Completed"
                                        : asg.status === "in_progress"
                                          ? "In Progress"
                                          : "Pending"}
                                    </span>
                                  </td>
                                  <td className="py-3.5 pr-4 text-center font-bold text-base sm:text-lg text-white whitespace-nowrap">
                                    {asg.status === "completed" ? (
                                      <button
                                        onClick={() => setSelectedAnswerSheetAssignment(asg)}
                                        className="text-emerald-400 hover:text-emerald-300 underline decoration-dashed font-black text-sm sm:text-base cursor-pointer transition hover:scale-105 inline-flex items-center gap-1"
                                        title="Click to view student answer sheet"
                                      >
                                        {asg.score !== null && asg.score !== undefined ? asg.score : "—"}
                                        <span className="text-xs text-gray-500 font-normal">
                                          / {asg.totalQuestions || "—"}
                                        </span>
                                      </button>
                                    ) : (
                                      <span className="text-gray-500 text-sm">—</span>
                                    )}
                                  </td>
                                  <td className="py-3.5 text-right whitespace-nowrap">
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                      {asg.status === "completed" ? (
                                        <button
                                          onClick={() => setSelectedAnswerSheetAssignment(asg)}
                                          className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-3 py-1.5 rounded-xl text-xs transition-all shadow-md shadow-emerald-600/20 flex items-center gap-1.5 hover:scale-105 active:scale-95"
                                          title="View Student Answer Sheet"
                                        >
                                          <FiFileText size={14} /> <span className="hidden sm:inline">View</span> Answer Sheet
                                        </button>
                                      ) : (
                                        <span className="text-[11px] text-gray-500 italic pr-1">
                                          Sheet locked (pending)
                                        </span>
                                      )}
                                      <button
                                        onClick={() => handleDeassignExam(asg.id)}
                                        className="bg-red-950/40 hover:bg-red-900/60 text-red-400 border border-red-900/40 font-bold px-3 py-1.5 rounded-xl text-xs transition-all flex items-center gap-1.5 ml-auto"
                                        title="Deassign / Delete Exam Assignment"
                                      >
                                        <FiTrash2 size={13} /> Deassign
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : activeTab === "add-question" ? (
                    <div className="bg-gray-800/50 backdrop-blur-md p-8 rounded-3xl border border-gray-700 shadow-xl space-y-6 animate-in fade-in slide-in-from-top-4 duration-250">
                      <div className="border-b border-gray-700 pb-4">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <FiPlus className="text-blue-400" /> Add Question Manually
                        </h3>
                        <p className="text-gray-400 text-sm mt-1">
                          Fill in the details below. Missing Hindi fields will be translated automatically using Google Translate.
                        </p>
                      </div>

                      <form onSubmit={handleSaveManualQuestion} className="space-y-6">
                        {/* Question Text */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                              Question Text (English) <span className="text-red-400">*</span>
                            </label>
                            <textarea
                              rows="3"
                              value={manualText}
                              onChange={(e) => setManualText(e.target.value)}
                              placeholder="Type the question in English..."
                              className="w-full bg-gray-900 border border-gray-700 rounded-2xl p-4 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                              Question Text (Hindi) <span className="text-gray-500">(Optional - Auto-translated if blank)</span>
                            </label>
                            <textarea
                              rows="3"
                              value={manualTextHindi}
                              onChange={(e) => setManualTextHindi(e.target.value)}
                              placeholder="Type the question in Hindi or leave blank to auto-translate..."
                              className="w-full bg-gray-900 border border-gray-700 rounded-2xl p-4 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                          </div>
                        </div>

                        {/* Options */}
                        <div className="space-y-4">
                          <h4 className="text-md font-bold text-gray-300 border-b border-gray-750 pb-2">Options</h4>

                          {/* Option A */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option A (English) <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptA}
                                onChange={(e) => setManualOptA(e.target.value)}
                                placeholder="Option A in English"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option A (Hindi) <span className="text-gray-500">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptAHindi}
                                onChange={(e) => setManualOptAHindi(e.target.value)}
                                placeholder="Option A in Hindi or leave blank"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              />
                            </div>
                          </div>

                          {/* Option B */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option B (English) <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptB}
                                onChange={(e) => setManualOptB(e.target.value)}
                                placeholder="Option B in English"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option B (Hindi) <span className="text-gray-500">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptBHindi}
                                onChange={(e) => setManualOptBHindi(e.target.value)}
                                placeholder="Option B in Hindi or leave blank"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              />
                            </div>
                          </div>

                          {/* Option C */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option C (English) <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptC}
                                onChange={(e) => setManualOptC(e.target.value)}
                                placeholder="Option C in English"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option C (Hindi) <span className="text-gray-500">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptCHindi}
                                onChange={(e) => setManualOptCHindi(e.target.value)}
                                placeholder="Option C in Hindi or leave blank"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              />
                            </div>
                          </div>

                          {/* Option D */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option D (English) <span className="text-red-400">*</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptD}
                                onChange={(e) => setManualOptD(e.target.value)}
                                placeholder="Option D in English"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                Option D (Hindi) <span className="text-gray-500">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={manualOptDHindi}
                                onChange={(e) => setManualOptDHindi(e.target.value)}
                                placeholder="Option D in Hindi or leave blank"
                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Correct Answer & Actions */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                          <div>
                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                              Correct Option <span className="text-red-400">*</span>
                            </label>
                            <select
                              value={manualCorrect}
                              onChange={(e) => setManualCorrect(e.target.value)}
                              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                              required
                            >
                              <option value="A">Option A</option>
                              <option value="B">Option B</option>
                              <option value="C">Option C</option>
                              <option value="D">Option D</option>
                            </select>
                          </div>

                          <div className="flex gap-4">
                            <button
                              type="button"
                              onClick={resetManualQuestionForm}
                              className="flex-1 bg-gray-750 hover:bg-gray-700 text-gray-200 border border-gray-700 font-bold py-3 px-6 rounded-xl transition-all"
                            >
                              Clear Form
                            </button>
                            <button
                              type="submit"
                              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98]"
                            >
                              Save Question
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <StudentExamPortal rollNumber={studentRollNumber} name={studentName} />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-gray-800 border border-gray-700 rounded-3xl p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
            <div className="flex justify-center mb-6">
              <div className="bg-red-500/20 p-4 rounded-full text-red-500">
                <FiTrash2 size={32} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-center mb-2">
              Confirm Deletion
            </h3>
            <p className="text-gray-400 text-center mb-8">
              {deleteModal.message}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteModal({ ...deleteModal, isOpen: false })
                }
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 rounded-xl font-bold text-white transition-all shadow-lg shadow-red-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {showEditQuestionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-gray-800 border border-gray-700 rounded-3xl p-6 sm:p-8 max-w-3xl w-full shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scroll">
            <div className="flex justify-between items-center border-b border-gray-700 pb-4 mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2 text-white">
                <FiEdit2 className="text-blue-400" /> Edit Question
              </h3>
              <button
                onClick={() => {
                  setShowEditQuestionModal(false);
                  setEditingQuestion(null);
                }}
                className="text-gray-400 hover:text-white transition-colors text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEditedQuestion} className="space-y-6">
              {/* Question Text */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Question Text (English) <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    rows="3"
                    value={editQText}
                    onChange={(e) => setEditQText(e.target.value)}
                    placeholder="Type the question in English..."
                    className="w-full bg-gray-900 border border-gray-700 rounded-2xl p-4 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Question Text (Hindi) <span className="text-gray-500">(Optional - Auto-translated if blank)</span>
                  </label>
                  <textarea
                    rows="3"
                    value={editQTextHindi}
                    onChange={(e) => setEditQTextHindi(e.target.value)}
                    placeholder="Type in Hindi or leave blank to auto-translate..."
                    className="w-full bg-gray-900 border border-gray-700 rounded-2xl p-4 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <h4 className="text-md font-bold text-gray-300 border-b border-gray-750 pb-2">Options</h4>

                {/* Option A */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option A (English) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={editQOptA}
                      onChange={(e) => setEditQOptA(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option A (Hindi)
                    </label>
                    <input
                      type="text"
                      value={editQOptAHindi}
                      onChange={(e) => setEditQOptAHindi(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                {/* Option B */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option B (English) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={editQOptB}
                      onChange={(e) => setEditQOptB(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option B (Hindi)
                    </label>
                    <input
                      type="text"
                      value={editQOptBHindi}
                      onChange={(e) => setEditQOptBHindi(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                {/* Option C */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option C (English) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={editQOptC}
                      onChange={(e) => setEditQOptC(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option C (Hindi)
                    </label>
                    <input
                      type="text"
                      value={editQOptCHindi}
                      onChange={(e) => setEditQOptCHindi(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                {/* Option D */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option D (English) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={editQOptD}
                      onChange={(e) => setEditQOptD(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Option D (Hindi)
                    </label>
                    <input
                      type="text"
                      value={editQOptDHindi}
                      onChange={(e) => setEditQOptDHindi(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Correct Answer Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Correct Answer <span className="text-red-400">*</span>
                </label>
                <select
                  value={editQCorrect}
                  onChange={(e) => setEditQCorrect(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all font-bold"
                >
                  <option value="A">Option A</option>
                  <option value="B">Option B</option>
                  <option value="C">Option C</option>
                  <option value="D">Option D</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditQuestionModal(false);
                    setEditingQuestion(null);
                  }}
                  className="flex-1 py-3 px-6 rounded-2xl bg-gray-700 hover:bg-gray-600 text-white font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Read-Only Answer Sheet Modal for Admin */}
      {selectedAnswerSheetAssignment && (
        <StudentAnswerSheetModal
          assignment={selectedAnswerSheetAssignment}
          onClose={() => setSelectedAnswerSheetAssignment(null)}
        />
      )}
    </div>
  );
};

export default ExamDashboard;
